package com.milta.feature.route.presentation

import com.milta.feature.route.domain.entity.RouteBatchDriverProposal

/**
 * Retour 26/10 (demande 3) — what the split preview says about each driver's zone and about the
 * balance between drivers (F1–F3), mobile `RouteBatchPreviewScreen` and web
 * `WebRouteBatchResultScreen` alike. Pure functions, no UI.
 */
object RouteBatchBalance {

    /** F2 — the gap is "balanced" up to 15 % of the longest working time. */
    const val BALANCED_GAP_RATIO = 0.15

    /** Number of cities named in a zone ("Zone Wavre, Ottignies"). */
    private const val ZONE_CITY_COUNT = 2

    // A 4-digit (BE, NL, LU) or 5-digit (FR, DE) postal code, then the city up to the next comma.
    // NL codes carry two letters after the digits ("1012 AB Amsterdam"): they are not the city.
    private val postalCodeCity = Regex("""(?<!\d)\d{4,5}(?!\d)(?:\s+[A-Z]{2}(?=\s))?\s+([^,\d][^,]*)""")

    /**
     * F1 — the city of [address]: the text after the postal code, up to the next comma. The last
     * postal code wins, so that a 4-digit house number ("Chaussée 1234 bte 2, 1300 Wavre") is
     * never read as one. Null when no postal code is followed by a city.
     */
    fun cityOf(address: String?): String? {
        if (address.isNullOrBlank()) return null
        return postalCodeCity.findAll(address).lastOrNull()
            ?.groupValues?.get(1)
            ?.trim()
            ?.takeIf { it.isNotEmpty() }
    }

    /**
     * F1 — the 2 most frequent cities among a driver's deliveries, in [addresses] order on a tie.
     * Cities are compared ignoring case and keep the spelling of their first occurrence. Empty when
     * no city is found (no address at all with an older backend): no zone line then.
     */
    fun zoneCities(addresses: List<String?>): List<String> {
        val counts = LinkedHashMap<String, Pair<String, Int>>()
        addresses.mapNotNull { cityOf(it) }.forEach { city ->
            val key = city.lowercase()
            val (spelling, count) = counts[key] ?: (city to 0)
            counts[key] = spelling to count + 1
        }
        // sortedByDescending is stable: equal counts keep their first-occurrence order.
        return counts.values.sortedByDescending { it.second }.take(ZONE_CITY_COUNT).map { it.first }
    }

    /**
     * F2 — the balance banner, or null when none is shown: fewer than 2 drivers with stops, or a
     * duration among them unknown (null, or 0 from a backend that did not compute it yet).
     */
    fun workloadBalance(proposals: List<RouteBatchDriverProposal>): WorkloadBalance? {
        val withStops = proposals.filter { it.deliveryCount > 0 }
        if (withStops.size < 2) return null
        val durations = withStops.map { it.durationMinutes?.takeIf { minutes -> minutes > 0 } ?: return null }
        val longest = durations.max()
        val gap = longest - durations.min()
        return if (gap <= longest * BALANCED_GAP_RATIO) WorkloadBalance.Balanced else WorkloadBalance.Unbalanced(gap)
    }

    /**
     * F3 — width of a driver's duration bar, `durationMinutes / longest duration` of the batch in
     * 0..1. Null (no bar) when that duration is unknown or 0.
     */
    fun durationRatio(proposal: RouteBatchDriverProposal, proposals: List<RouteBatchDriverProposal>): Float? {
        val minutes = proposal.durationMinutes?.takeIf { it > 0 } ?: return null
        val longest = proposals.maxOf { it.durationMinutes ?: 0 }
        return (minutes.toFloat() / longest).coerceIn(0f, 1f)
    }

    /** "1h05" / "27 min" — same format as the durations on the driver cards. */
    fun formatDuration(minutes: Int): String {
        val h = minutes / 60
        val m = minutes % 60
        return if (h > 0) "${h}h${m.toString().padStart(2, '0')}" else "$m min"
    }
}

/** F2 — green "équilibrés" banner, or orange one with the gap between longest and shortest. */
sealed interface WorkloadBalance {
    data object Balanced : WorkloadBalance
    data class Unbalanced(val gapMinutes: Int) : WorkloadBalance
}
